import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import {EVI, Visible, NDVI, Altimetry, SoilMap, YieldMap} from './Services'
import {viewImageState} from './ViewImageState'
import PopupEvent from './Services/PopupEvent/PopupEvent'
import {fromBlob} from 'geotiff'
import {useMapLayersV2} from '../../../../Map/MapLayersV2Context'
import {useMapLegendContext} from '../Helpers/MapLegend'
import * as GisDataAdapter from '../../../services/GisDataAdapter'
import {useViewLandContext} from '../ViewLandContext'
import HdAccessConfirmModal from './HdAccessConfirmModal'
import {PLANET_HD_V1_ENABLED} from '../../../../../../config/featureFlags'
import {useAuth} from '../../../../../auth/core/Auth'
import {
  HD_IMAGE_TYPES,
  IMAGE_TYPES_WITH_DATE,
  STANDARD_IMAGE_TYPES,
  getImageQuality,
  getMapLayerImageType,
  isHdAccessActive,
  isHdImageType,
  isImageAmbientSourceType,
} from './imageTypeUtils'
import {
  REMOTE_SENSING_LAYERS,
  REMOTE_SENSING_PRODUCTS,
  normalizeRemoteSensingSource,
} from './remoteSensingModel'
import {
  PLANET_HD_ACCESS_STATES,
  canConfirmPlanetHdAccess,
  canUsePlanetHd,
  getPlanetHdAccessState,
} from './planetHdAccessModel'
import {
  doesPlanetHdSelectionMatchAccess,
  getPlanetHdAccessIdentity,
  getPlanetHdCompanyPeriodId,
} from './planetHdAccessIdentity'
import {
  REMOTE_SENSING_DATE_STATES,
  normalizeRemoteSensingDateItem,
  normalizeRemoteSensingDateItems,
} from './remoteSensingDateModel'
import {buildRemoteSensingDatesCacheKey} from './remoteSensingCacheKey'
import {
  buildPlanetHdAcquisitionQuery,
  getPlanetHdAcquisitionRange,
  isPlanetHdDateAllowed,
} from './planetHdAcquisitionQuery'
import {
  clearPlanetHdRequest,
  getOrCreatePlanetHdConfirmRequest,
  getOrCreatePlanetHdPrepareRequest,
} from './planetHdRequestId'
import {
  DEFAULT_IMAGE_LAYER_LOAD_STATE,
  markImageLayerReady,
  resetImageLayerLoadState,
} from './imageLayerLoadState'
import {
  doesHdAccessMatchGeometry,
  hasLandGeometryChanged,
  omitLandCacheEntries,
} from './landGeometryState'
import {DEFAULT_IMAGE_CLOUDINESS, getImageCloudinessKey} from './imageCloudinessPreferences'

const DEFAULT_DATES_STATE = {
  status: 'idle',
  availableDates: 0,
  cloudyDates: 0,
  cloudinessFilter: null,
}

const EMPTY_ACTIVE_LAND_IMAGE = {
  land: null,
  imageType: null,
  selectedDate: null,
}

const getDatesCacheKey = (landId, quality = 'standard') => `${landId}:${quality || 'standard'}`

const hasRenderableLandLayer = (landImage) => {
  if (landImage?.imageType == null) {
    return false
  }

  if (landImage.imageType === 'altimetry') {
    return true
  }

  if (landImage.imageType === STANDARD_IMAGE_TYPES.SOIL_MAP) {
    return true
  }

  if (landImage.imageType === STANDARD_IMAGE_TYPES.YIELD_MAP) {
    return landImage.selectedYieldMapId != null
  }

  if (isHdImageType(landImage.imageType) && !isPlanetHdDateAllowed(landImage.selectedDate)) {
    return false
  }

  return landImage.selectedDate != null
}

const loadLandDates = async (landId, filters) => {
  try {
    const res = await GisDataAdapter.getLandDates(landId, filters)
    return Array.isArray(res?.data) ? res.data : []
  } catch (error) {
    if (error?.response?.status === 404) {
      return []
    }

    throw error
  }
}

const ViewImageContext = createContext()

export function useViewImageContext() {
  return useContext(ViewImageContext)
}

export const ViewImageConsumer = ViewImageContext.Consumer

export function ViewImageProvider({children}) {
  const {auth, revalidateAuthentication} = useAuth()
  const [hasImageActive, setImageActive] = useState(false)
  const [imageType] = useState()
  const [dates, setDates] = useState([])
  const [cloudyDates, setCloudyDates] = useState([])
  const [imageCloudinessByScope, setImageCloudinessByScope] = useState({})
  const [datesStateByLand, setDatesStateByLand] = useState({})
  const [datesCacheByLand, setDatesCacheByLand] = useState({})
  const [yieldMapsByLand, setYieldMapsByLand] = useState({})
  const [yieldMapsLoadingByLand, setYieldMapsLoadingByLand] = useState({})
  const [soilMapsByLand, setSoilMapsByLand] = useState({})
  const [soilMapsLoadingByLand, setSoilMapsLoadingByLand] = useState({})
  const [selectedDate] = useState(null)
  const [activeLands, setActiveLands] = useState({})
  const [loading, setLoading] = useState(false)
  const [imageLayerLoadByLand, setImageLayerLoadByLand] = useState({})
  const [openPrescription, setOpenPrescription] = useState(false)
  const [openImageAmbient, setOpenImageAmbient] = useState(false)
  const [prescriptionAmbient, setPrescriptionAmbient] = useState(null)
  const [openedAmbients, setOpenedAmbients] = useState([])
  const [clearMapVersion, setClearMapVersion] = useState(0)
  const [activeAmbientSelection, setActiveAmbientSelection] = useState(null)
  const [hasComparisonMapOverlay, setHasComparisonMapOverlay] = useState(false)
  const [hdAccessByLand, setHdAccessByLand] = useState({})
  const [hdAccessLoadingByLand, setHdAccessLoadingByLand] = useState({})
  const [hdAcquisitionsByKey, setHdAcquisitionsByKey] = useState({})
  const [hdConfirmState, setHdConfirmState] = useState({
    open: false,
    confirming: false,
    land: null,
    operation: null,
    context: null,
    access: null,
    request: null,
    error: null,
  })
  const mapLayers = useMapLayersV2()
  const mapLegendContext = useMapLegendContext()
  const viewLandContext = useViewLandContext()
  const pendingDatesRequests = useRef({})
  const pendingYieldMapRequests = useRef({})
  const pendingSoilMapRequests = useRef({})
  const pendingHdAccessRequests = useRef({})
  const pendingHdActionRef = useRef(null)
  const pendingHdAcquisitionRequests = useRef({})
  const hdAccessByLandRef = useRef({})
  const hdAccessRequestVersionByLand = useRef({})
  const hdGeometryHashByLandRef = useRef({})
  const imageLayerAttemptByLand = useRef({})
  const previousSectionRef = useRef(viewLandContext?.activeSection)
  const comparisonResetRef = useRef(null)

  const getImageCloudinessFilter = useCallback((land, product) => (
    imageCloudinessByScope[getImageCloudinessKey(land, product)] ?? DEFAULT_IMAGE_CLOUDINESS
  ), [imageCloudinessByScope])

  const setImageCloudinessFilter = useCallback((land, product, value) => {
    if (!land?.land_id || !Object.values(REMOTE_SENSING_PRODUCTS).includes(product) ||
        typeof value !== 'number' || !Number.isFinite(value)) return
    const key = getImageCloudinessKey(land, product)
    const bounded = Math.min(100, Math.max(0, value))
    setImageCloudinessByScope((previous) => previous[key] === bounded
      ? previous : {...previous, [key]: bounded})
  }, [])

  const setDatesState = useCallback((landId, nextState, quality = 'standard') => {
    if (!landId) {
      return
    }

    const cacheKey = getDatesCacheKey(landId, quality)

    setDatesStateByLand((prev) => ({
      ...prev,
      [cacheKey]: {
        ...(prev[cacheKey] || {}),
        ...nextState,
        quality,
      },
    }))
  }, [])

  const getDatesState = useCallback(
    (landId, quality = 'standard') => {
      if (!landId) {
        return DEFAULT_DATES_STATE
      }

      return datesStateByLand[getDatesCacheKey(landId, quality)] || DEFAULT_DATES_STATE
    },
    [datesStateByLand]
  )

  const getActiveLandImage = useCallback(
    (landId) => {
      if (!landId) {
        return EMPTY_ACTIVE_LAND_IMAGE
      }

      const active = activeLands[landId] || EMPTY_ACTIVE_LAND_IMAGE
      return isHdImageType(active.imageType) && !isPlanetHdDateAllowed(active.selectedDate)
        ? {...active, selectedDate: null, selectedAcquisitionId: null}
        : active
    },
    [activeLands]
  )

  const getRememberedRemoteSensingSelection = useCallback(
    (landId, product) => viewImageState.getRemoteSensingSelection(landId, product),
    []
  )

  const getImageLayerLoadState = useCallback(
    (landId) => imageLayerLoadByLand[landId] || DEFAULT_IMAGE_LAYER_LOAD_STATE,
    [imageLayerLoadByLand]
  )

  const claimImageLayerAttempt = useCallback((landId, attemptKey) => {
    if (!landId || !attemptKey || imageLayerAttemptByLand.current[landId] === attemptKey) {
      return false
    }

    imageLayerAttemptByLand.current[landId] = attemptKey
    return true
  }, [])

  const releaseImageLayerAttempt = useCallback((landId, attemptKey) => {
    if (imageLayerAttemptByLand.current[landId] === attemptKey) {
      delete imageLayerAttemptByLand.current[landId]
    }
  }, [])

  const setImageLayerLoading = useCallback((landId, requestKey) => {
    if (!landId || !requestKey) {
      return
    }

    setImageLayerLoadByLand((prev) => {
      const current = prev[landId] || DEFAULT_IMAGE_LAYER_LOAD_STATE

      if (current.status === 'loading' && current.requestKey === requestKey) {
        return prev
      }

      return {
        ...prev,
        [landId]: {
          ...current,
          status: 'loading',
          requestKey,
          errorType: null,
        },
      }
    })
  }, [])

  const setImageLayerReady = useCallback((landId, requestKey) => {
    setImageLayerLoadByLand((prev) => markImageLayerReady(prev, landId, requestKey))
  }, [])

  const setImageLayerError = useCallback((landId, requestKey, result = {}) => {
    setImageLayerLoadByLand((prev) => {
      const current = prev[landId] || DEFAULT_IMAGE_LAYER_LOAD_STATE

      if (current.requestKey !== requestKey) {
        return prev
      }

      return {
        ...prev,
        [landId]: {
          ...current,
          status: 'error',
          errorType: result?.error_type || 'internal_error',
        },
      }
    })
  }, [])

  const cancelImageLayerLoad = useCallback((landId, requestKey) => {
    setImageLayerLoadByLand((prev) => {
      const current = prev[landId]

      if (!current || current.requestKey !== requestKey || current.status !== 'loading') {
        return prev
      }

      return {
        ...prev,
        [landId]: {
          ...current,
          status: 'idle',
          errorType: null,
        },
      }
    })
  }, [])

  const retryImageLayer = useCallback((landId) => {
    if (!landId) {
      return
    }

    delete imageLayerAttemptByLand.current[landId]
    setImageLayerLoadByLand((prev) => {
      const current = prev[landId] || DEFAULT_IMAGE_LAYER_LOAD_STATE

      return {
        ...prev,
        [landId]: {
          ...current,
          status: 'idle',
          retryVersion: current.retryVersion + 1,
          errorType: null,
        },
      }
    })
  }, [])

  const resetImageLayerLoad = useCallback((landId) => {
    if (!landId) {
      return
    }

    delete imageLayerAttemptByLand.current[landId]
    setImageLayerLoadByLand((prev) => resetImageLayerLoadState(prev, landId))
  }, [])

  const setLandDatesCache = useCallback((landId, nextState, quality = 'standard') => {
    if (!landId) {
      return
    }

    const cacheKey = getDatesCacheKey(landId, quality)

    setDatesCacheByLand((prev) => ({
      ...prev,
      [cacheKey]: {
        ...nextState,
        quality,
      },
    }))
  }, [])

  const getLandDatesCache = useCallback(
    (landId, cloudinessFilter, quality = 'standard') => {
      if (!landId) {
        return null
      }

      const cache = datesCacheByLand[getDatesCacheKey(landId, quality)]
      if (!cache) {
        return null
      }

      if (cloudinessFilter != null && cache.cloudinessFilter !== cloudinessFilter) {
        return null
      }

      return cache
    },
    [datesCacheByLand]
  )

  const getLandDatesCollections = useCallback(
    (landId, cloudinessFilter = null, quality = 'standard') => {
      const cache = getLandDatesCache(landId, cloudinessFilter, quality)

      return {
        clearDates: cache?.clearDates || [],
        cloudyDatesList: cache?.cloudyDatesList || [],
      }
    },
    [getLandDatesCache]
  )

  const setYieldMapsLoading = useCallback((landId, nextLoading) => {
    if (!landId) {
      return
    }

    setYieldMapsLoadingByLand((prev) => ({
      ...prev,
      [landId]: nextLoading,
    }))
  }, [])

  const setLandYieldMaps = useCallback((landId, maps) => {
    if (!landId) {
      return
    }

    const sortedMaps = [...(maps || [])].sort((left, right) => {
      const leftCampaign = String(left.campaign || '')
      const rightCampaign = String(right.campaign || '')

      if (leftCampaign !== rightCampaign) {
        return rightCampaign.localeCompare(leftCampaign)
      }

      return String(right.createdAt || '').localeCompare(String(left.createdAt || ''))
    })

    setYieldMapsByLand((prev) => ({
      ...prev,
      [landId]: sortedMaps,
    }))
  }, [])

  const ensureLandYieldMaps = useCallback(
    async (landId, {force = false} = {}) => {
      if (!landId) {
        return []
      }

      const cached = yieldMapsByLand[landId]
      if (!force && Array.isArray(cached)) {
        return cached
      }

      if (pendingYieldMapRequests.current[landId]) {
        return pendingYieldMapRequests.current[landId]
      }

      setYieldMapsLoading(landId, true)
      const request = GisDataAdapter.listLandYieldMaps(landId)
        .then((res) => {
          const maps = res?.ok ? res.data?.yieldMaps || [] : []
          setLandYieldMaps(landId, maps)
          return maps
        })
        .catch(() => {
          setLandYieldMaps(landId, [])
          return []
        })
        .finally(() => {
          setYieldMapsLoading(landId, false)
          delete pendingYieldMapRequests.current[landId]
        })

      pendingYieldMapRequests.current[landId] = request
      return request
    },
    [setLandYieldMaps, setYieldMapsLoading, yieldMapsByLand]
  )

  const getLandYieldMapsState = useCallback(
    (landId) => ({
      yieldMaps: yieldMapsByLand[landId] || [],
      loading: Boolean(yieldMapsLoadingByLand[landId]),
    }),
    [yieldMapsByLand, yieldMapsLoadingByLand]
  )

  const setSoilMapLoading = useCallback((landId, nextLoading) => {
    if (!landId) {
      return
    }

    setSoilMapsLoadingByLand((prev) => ({
      ...prev,
      [landId]: nextLoading,
    }))
  }, [])

  const setLandSoilMap = useCallback((landId, soilMap) => {
    if (!landId) {
      return
    }

    setSoilMapsByLand((prev) => ({
      ...prev,
      [landId]: soilMap,
    }))
  }, [])

  const ensureLandSoilMap = useCallback(
    async (landId, {force = false} = {}) => {
      if (!landId) {
        return null
      }

      const cached = soilMapsByLand[landId]
      if (!force && cached) {
        return cached
      }

      if (pendingSoilMapRequests.current[landId]) {
        return pendingSoilMapRequests.current[landId]
      }

      setSoilMapLoading(landId, true)
      const request = GisDataAdapter.getLandSoilMap(landId)
        .then((res) => {
          const soilMap = res?.ok && res.data
            ? res.data
            : {
                status: 'source_unavailable',
                reason: 'invalid_response',
              }
          setLandSoilMap(landId, soilMap)
          return soilMap
        })
        .catch(() => {
          const soilMap = {
            status: 'source_unavailable',
            reason: 'request_failed',
          }
          setLandSoilMap(landId, soilMap)
          return soilMap
        })
        .finally(() => {
          setSoilMapLoading(landId, false)
          delete pendingSoilMapRequests.current[landId]
        })

      pendingSoilMapRequests.current[landId] = request
      return request
    },
    [setLandSoilMap, setSoilMapLoading, soilMapsByLand]
  )

  const getLandSoilMapState = useCallback(
    (landId) => {
      const data = landId ? soilMapsByLand[landId] || null : null

      return {
        data,
        status: data?.status || 'idle',
        reason: data?.reason || null,
        loading: Boolean(landId && soilMapsLoadingByLand[landId]),
      }
    },
    [soilMapsByLand, soilMapsLoadingByLand]
  )

  const setHdAccessState = useCallback((landId, nextAccess) => {
    if (!landId) {
      return
    }

    const previous = hdAccessByLandRef.current[landId]
    const selection = viewImageState.getLand(landId)
    const changed = previous && getPlanetHdAccessIdentity(previous) !== getPlanetHdAccessIdentity(nextAccess)
    const invalidSelection = selection?.selectedAcquisitionId && !doesPlanetHdSelectionMatchAccess({
      accessId: selection.selectedAccessId,
      companyPeriodId: selection.selectedCompanyPeriodId,
    }, nextAccess)
    if (changed || invalidSelection || (canUsePlanetHd(previous) && !canUsePlanetHd(nextAccess))) {
      viewImageState.invalidatePlanetHdAccess(landId)
      setHdAcquisitionsByKey(prev => omitLandCacheEntries(prev, landId))
      if (isHdImageType(selection?.imageType)) {
        mapLayers.land.deleteImage({landId, imageType: getMapLayerImageType(selection.imageType)})
        mapLegendContext.deleteLegend(landId)
        resetImageLayerLoad(landId)
        setActiveLands(prev => !prev[landId] ? prev : ({
          ...prev,
          [landId]: {
            ...prev[landId],
            selectedDate: null,
            selectedAcquisitionId: null,
            selectedAcquisitionStatus: null,
            selectedAccessId: null,
            selectedCompanyPeriodId: null,
            selectedItemId: null,
            selectedQualityWarning: null,
            remoteSensingSource: null,
          },
        }))
      }
      if (viewLandContext?.land?.land_id === landId) comparisonResetRef.current?.()
    }
    hdAccessByLandRef.current[landId] = nextAccess
    setHdAccessByLand((prev) => ({
      ...prev,
      [landId]: nextAccess,
    }))
  }, [mapLayers.land, mapLegendContext, resetImageLayerLoad, viewLandContext?.land?.land_id])

  const beginHdAccessRequest = useCallback(landId => {
    const version = (hdAccessRequestVersionByLand.current[landId] || 0) + 1
    hdAccessRequestVersionByLand.current[landId] = version
    return version
  }, [])

  const setHdAccessLoading = useCallback((landId, loading) => {
    if (!landId) {
      return
    }

    setHdAccessLoadingByLand((prev) => ({
      ...prev,
      [landId]: loading,
    }))
  }, [])

  const ensureLandHdAccess = useCallback(
    async (landId, options = {}) => {
      if (!PLANET_HD_V1_ENABLED || !landId) {
        return null
      }

      const {
        force = false,
        geometryHash = '',
        operation = 'view_planet_hd',
        context = null,
        background = false,
      } = options
      const cached = hdAccessByLandRef.current[landId]

      if (!force && cached && canUsePlanetHd(cached) && doesHdAccessMatchGeometry(cached, geometryHash)) {
        return cached
      }

      const requestKey = [
        landId,
        geometryHash,
        operation || 'default',
        JSON.stringify(context || {}),
      ].join(':')
      const pending = pendingHdAccessRequests.current[requestKey]
      if (pending && pending.version === hdAccessRequestVersionByLand.current[landId]) {
        return pending.request
      }

      const version = beginHdAccessRequest(landId)
      if (!background) setHdAccessLoading(landId, true)

      const request = GisDataAdapter.getLandHdAccess(landId, {
        operation,
        ...(geometryHash ? {geometry_hash: geometryHash} : {}),
        ...context,
      })
        .then((res) => {
          const access = res?.ok ? res.data : null
          if (hdAccessRequestVersionByLand.current[landId] !== version) return null
          if (background && !res?.ok) return cached
          if (
            geometryHash &&
            hdGeometryHashByLandRef.current[landId] &&
            hdGeometryHashByLandRef.current[landId] !== geometryHash
          ) {
            return null
          }
          setHdAccessState(landId, access)
          return access
        })
        .catch(() => {
          if (hdAccessRequestVersionByLand.current[landId] !== version) return null
          if (background) return cached
          if (
            geometryHash &&
            hdGeometryHashByLandRef.current[landId] &&
            hdGeometryHashByLandRef.current[landId] !== geometryHash
          ) {
            return null
          }
          setHdAccessState(landId, null)
          return null
        })
        .finally(() => {
          if (hdAccessRequestVersionByLand.current[landId] !== version) return
          if (
            !geometryHash ||
            !hdGeometryHashByLandRef.current[landId] ||
            hdGeometryHashByLandRef.current[landId] === geometryHash
          ) {
            if (!background) setHdAccessLoading(landId, false)
          }
          delete pendingHdAccessRequests.current[requestKey]
        })

      pendingHdAccessRequests.current[requestKey] = {request, version}
      return request
    },
    [beginHdAccessRequest, setHdAccessLoading, setHdAccessState]
  )

  const getHdAccessStatus = useCallback(
    (landId) => {
      const access = landId ? hdAccessByLand[landId] : null
      const loading = Boolean(landId && hdAccessLoadingByLand[landId])

      const state = PLANET_HD_V1_ENABLED
        ? getPlanetHdAccessState(access)
        : (isHdAccessActive(access)
          ? PLANET_HD_ACCESS_STATES.READY
          : PLANET_HD_ACCESS_STATES.UNAVAILABLE)

      return {
        access,
        loading,
        state,
        featureEnabled: PLANET_HD_V1_ENABLED,
        canConfirm: PLANET_HD_V1_ENABLED && canConfirmPlanetHdAccess(access),
        enabled: PLANET_HD_V1_ENABLED ? canUsePlanetHd(access) : isHdAccessActive(access),
      }
    },
    [hdAccessByLand, hdAccessLoadingByLand]
  )

  const cancelHdAccessConfirmation = useCallback(() => {
    pendingHdActionRef.current?.resolve?.(false)
    pendingHdActionRef.current = null
    setHdConfirmState({
      open: false,
      confirming: false,
      land: null,
      operation: null,
      context: null,
      access: null,
      request: null,
      error: null,
    })
  }, [])

  const requestHdAccess = useCallback(
    async ({land, operation, context = {}, onConfirmed, onUnavailable}) => {
      if (
        !PLANET_HD_V1_ENABLED ||
        !land?.land_id ||
        typeof onConfirmed !== 'function'
      ) {
        return false
      }

      const product = REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
      const requestContext = {
        operation,
        ...context,
        product,
      }
      const version = beginHdAccessRequest(land.land_id)
      setHdAccessLoading(land.land_id, true)
      let preflightError = null
      const access = await GisDataAdapter.preflightLandHdAccess(land.land_id, {
        ...requestContext,
      }).then((res) => {
        if (hdAccessRequestVersionByLand.current[land.land_id] !== version) return null
        const nextAccess = res?.ok ? res.data : null
        setHdAccessState(land.land_id, nextAccess)
        return nextAccess
      }).catch((error) => {
        preflightError = error
        return null
      }).finally(() => {
        if (hdAccessRequestVersionByLand.current[land.land_id] === version) {
          setHdAccessLoading(land.land_id, false)
        }
      })

      if (canUsePlanetHd(access)) {
        const result = await onConfirmed(access)
        return result ?? true
      }

      if (!canConfirmPlanetHdAccess(access)) {
        await onUnavailable?.({access, error: preflightError})
        return false
      }

      return new Promise((resolve) => {
        pendingHdActionRef.current?.resolve?.(false)
        pendingHdActionRef.current = {
          onConfirmed,
          resolve,
        }

        const request = getOrCreatePlanetHdConfirmRequest({
          companyId: land.company_id || access?.company_id || '',
          companyPeriodId: getPlanetHdCompanyPeriodId(access),
          geometryHash:
            land.geometry_hash ||
            land.geometryHash ||
            access?.geometry_hash ||
            '',
          landId: land.land_id,
          product,
        })

        setHdConfirmState({
          open: true,
          confirming: false,
          land,
          operation,
          context,
          access,
          request,
          error: null,
        })
      })
    },
    [beginHdAccessRequest, setHdAccessLoading, setHdAccessState]
  )

  const confirmHdAccess = useCallback(async () => {
    const landId = hdConfirmState.land?.land_id

    if (!landId || hdConfirmState.confirming) {
      return
    }

    setHdConfirmState((prev) => ({
      ...prev,
      confirming: true,
    }))

    const version = beginHdAccessRequest(landId)
    const ignoreStaleConfirmation = () => {
      if (hdAccessRequestVersionByLand.current[landId] === version) return false
      setHdConfirmState(prev => prev.request?.requestId === hdConfirmState.request?.requestId
        ? {...prev, confirming: false, error: 'confirmation_failed'}
        : prev)
      return true
    }
    try {
      const res = await GisDataAdapter.confirmLandHdAccess(landId, {
        operation: hdConfirmState.operation,
        ...hdConfirmState.context,
        company_id: hdConfirmState.land?.company_id || hdConfirmState.access?.company_id,
        product: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
        idempotency_key: hdConfirmState.request?.requestId,
        expected_company_period_id: getPlanetHdCompanyPeriodId(hdConfirmState.access) || undefined,
      })

      if (ignoreStaleConfirmation()) return
      const access = res?.ok ? res.data : null
      const accessState = getPlanetHdAccessState(access)
      const validPlanetHdConfirmation = Boolean(
        access?.access_id && [
          PLANET_HD_ACCESS_STATES.PROVISIONING,
          PLANET_HD_ACCESS_STATES.READY,
        ].includes(accessState)
      )

      if (PLANET_HD_V1_ENABLED && !validPlanetHdConfirmation) {
        setHdConfirmState((prev) => ({
          ...prev,
          confirming: false,
          error: 'confirmation_failed',
        }))
        return
      }

      const pending = pendingHdActionRef.current
      pendingHdActionRef.current = null

      if (hdConfirmState.request?.storageKey) {
        clearPlanetHdRequest(hdConfirmState.request.storageKey)
      }

      setHdAccessState(landId, access)
      setHdConfirmState({
        open: false,
        confirming: false,
        land: null,
        operation: null,
        context: null,
        access: null,
        request: null,
        error: null,
      })

      try {
        if (!PLANET_HD_V1_ENABLED || accessState === PLANET_HD_ACCESS_STATES.READY) {
          const result = await pending?.onConfirmed?.(access)
          pending?.resolve?.(result ?? true)
        } else {
          pending?.resolve?.(false)
        }
      } catch (actionError) {
        pending?.resolve?.(false)
      }
    } catch (error) {
      if (ignoreStaleConfirmation()) return
      setHdConfirmState((prev) => ({
        ...prev,
        confirming: false,
        error: 'confirmation_failed',
      }))
    }
  }, [beginHdAccessRequest, hdConfirmState, setHdAccessState])

  const getLandHdAcquisitionsState = useCallback((
    land,
    source,
    cloudinessFilter = 20,
    options = {}
  ) => {
    if (!land?.land_id) {
      return {status: 'idle', items: []}
    }

    const access = hdAccessByLand[land.land_id]
    if (!canUsePlanetHd(access)) return {status: 'access_not_ready', items: []}

    const normalizedSource = normalizeRemoteSensingSource(source)
    const range = getPlanetHdAcquisitionRange(options)
    const cacheKey = buildRemoteSensingDatesCacheKey({
      accessId: access.access_id,
      companyPeriodId: getPlanetHdCompanyPeriodId(access),
      cloudCoverage: cloudinessFilter,
      cloudCoverageFilter: cloudinessFilter == null ? 'all' : 'below',
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      geometryHash: access.geometry_hash || land.geometry_hash || '',
      landId: land.land_id,
      source: {product: normalizedSource.product, layer: REMOTE_SENSING_LAYERS.VISIBLE},
    })

    return hdAcquisitionsByKey[cacheKey] || {status: 'idle', items: []}
  }, [hdAccessByLand, hdAcquisitionsByKey])

  const ensureLandHdAcquisitions = useCallback(async (
    land,
    source,
    cloudinessFilter = 20,
    {force = false, dateFrom, dateTo} = {}
  ) => {
    if (!PLANET_HD_V1_ENABLED || !land?.land_id) {
      return {status: 'idle', items: []}
    }

    const access = hdAccessByLandRef.current[land.land_id]
    if (!canUsePlanetHd(access)) {
      return {status: 'access_not_ready', items: []}
    }

    const normalizedSource = normalizeRemoteSensingSource(source)
    const range = getPlanetHdAcquisitionRange({dateFrom, dateTo})
    const accessIdentity = getPlanetHdAccessIdentity(access)
    const companyPeriodId = getPlanetHdCompanyPeriodId(access)
    const cacheKey = buildRemoteSensingDatesCacheKey({
      accessId: access.access_id,
      companyPeriodId,
      cloudCoverage: cloudinessFilter,
      cloudCoverageFilter: cloudinessFilter == null ? 'all' : 'below',
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      geometryHash: access?.geometry_hash || land.geometry_hash || '',
      landId: land.land_id,
      source: {product: normalizedSource.product, layer: REMOTE_SENSING_LAYERS.VISIBLE},
    })
    const cached = hdAcquisitionsByKey[cacheKey]
    // A failed catalog remains settled until an explicit retry or a new query.
    // Otherwise the selector effect retries whenever this state recreates us.
    if (!force && ['ready', 'empty', 'error'].includes(cached?.status)) {
      return cached
    }
    if (pendingHdAcquisitionRequests.current[cacheKey]) {
      return pendingHdAcquisitionRequests.current[cacheKey]
    }

    const hasCachedItems = Array.isArray(cached?.items) && cached.items.length > 0
    setHdAcquisitionsByKey((prev) => ({
      ...prev,
      [cacheKey]: hasCachedItems
        ? {...cached, refreshing: true}
        : {...(prev[cacheKey] || {}), status: 'loading', refreshing: false, items: []},
    }))

    const requestToken = auth?.api_token
    const request = GisDataAdapter.searchLandHdAcquisitions(land.land_id, {
      ...buildPlanetHdAcquisitionQuery({
        cloudPercent: cloudinessFilter,
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      }),
    }).then((res) => {
      if (getPlanetHdAccessIdentity(hdAccessByLandRef.current[land.land_id]) !== accessIdentity) {
        return {status: 'access_not_ready', items: []}
      }
      if (!res?.ok) {
        throw new Error('planet-hd-acquisitions-load-failed')
      }
      const items = normalizeRemoteSensingDateItems(res.data, normalizedSource).map(item => ({
        ...item,
        accessId: item.accessId || access.access_id,
        companyPeriodId,
      }))
      const nextState = {
        status: items.length > 0 ? 'ready' : 'empty',
        items,
        source: normalizedSource,
        cloudinessFilter,
      }
      setHdAcquisitionsByKey((prev) => ({...prev, [cacheKey]: nextState}))
      return nextState
    }).catch(async (error) => {
      if (getPlanetHdAccessIdentity(hdAccessByLandRef.current[land.land_id]) !== accessIdentity) {
        return {status: 'access_not_ready', items: []}
      }
      const nextState = {status: 'error', items: cached?.items || [], error}
      setHdAcquisitionsByKey((prev) => ({...prev, [cacheKey]: nextState}))
      await revalidateAuthentication(error, requestToken)
      return nextState
    }).finally(() => {
      delete pendingHdAcquisitionRequests.current[cacheKey]
    })

    pendingHdAcquisitionRequests.current[cacheKey] = request
    return request
  }, [auth?.api_token, revalidateAuthentication, hdAccessByLand, hdAcquisitionsByKey])

  const prepareLandHdAcquisition = useCallback(async (land, item, source) => {
    const access = hdAccessByLandRef.current[land?.land_id]
    const normalizedSource = normalizeRemoteSensingSource(source)
    if (
      !PLANET_HD_V1_ENABLED ||
      !land?.land_id ||
      !item?.acquisitionId ||
      !isPlanetHdDateAllowed(item.date) ||
      !canUsePlanetHd(access) ||
      !doesPlanetHdSelectionMatchAccess(item, access)
    ) {
      return null
    }

    const request = getOrCreatePlanetHdPrepareRequest({
      accessId: access.access_id,
      companyPeriodId: getPlanetHdCompanyPeriodId(access),
      acquisitionId: item.acquisitionId,
      geometryHash: access.geometry_hash || '',
      landId: land.land_id,
      layer: normalizedSource.layer,
    })
    const res = await GisDataAdapter.prepareLandHdAcquisition(
      land.land_id,
      item.acquisitionId,
      {
        idempotency_key: request.requestId,
        expected_company_period_id: getPlanetHdCompanyPeriodId(access) || undefined,
      }
    )

    if (!res?.ok || getPlanetHdAccessIdentity(hdAccessByLandRef.current[land.land_id]) !== getPlanetHdAccessIdentity(access)) {
      return null
    }

    clearPlanetHdRequest(request.storageKey)
    return {...normalizeRemoteSensingDateItem(
      res.data || {...item.raw, status: REMOTE_SENSING_DATE_STATES.PREPARING},
      normalizedSource
    ), accessId: access.access_id, companyPeriodId: getPlanetHdCompanyPeriodId(access)}
  }, [hdAccessByLand])

  const ensureLandDates = useCallback(
    async (landId, cloudinessFilter = 20, quality = 'standard', {force = false} = {}) => {
      if (!landId) {
        return {
          ...DEFAULT_DATES_STATE,
          clearDates: [],
          cloudyDatesList: [],
        }
      }

      const cacheKey = getDatesCacheKey(landId, quality)
      const cached = datesCacheByLand[cacheKey]
      if (
        !force &&
        cached &&
        cached.cloudinessFilter === cloudinessFilter &&
        (cached.status === 'ready' || cached.status === 'empty' || cached.status === 'error')
      ) {
        return cached
      }

      const requestKey = `${landId}:${quality}:${cloudinessFilter}`
      if (pendingDatesRequests.current[requestKey]) {
        return pendingDatesRequests.current[requestKey]
      }

      setDatesState(landId, {
        status: 'loading',
        availableDates: cached?.availableDates || 0,
        cloudyDates: cached?.cloudyDates || 0,
        cloudinessFilter,
      }, quality)

      const request = Promise.all([
        loadLandDates(landId, {
          cloud_coverage_filter: 'below',
          cloud_coverage: cloudinessFilter,
          quality,
        }),
        loadLandDates(landId, {
          cloud_coverage_filter: 'above',
          cloud_coverage: cloudinessFilter,
          quality,
        }),
      ])
        .then(([clearDates, cloudyDatesList]) => {
          const availableDates = clearDates.length
          const cloudyDates = cloudyDatesList.length
          const nextState = {
            status: availableDates > 0 || cloudyDates > 0 ? 'ready' : 'empty',
            availableDates,
            cloudyDates,
            cloudinessFilter,
            clearDates,
            cloudyDatesList,
          }

          setDatesState(landId, nextState, quality)
          setLandDatesCache(landId, nextState, quality)
          return nextState
        })
        .catch((error) => {
          const nextState = {
            status: 'error',
            availableDates: 0,
            cloudyDates: 0,
            cloudinessFilter,
            clearDates: [],
            cloudyDatesList: [],
          }

          setDatesState(landId, nextState, quality)
          setLandDatesCache(landId, nextState, quality)
          throw error
        })
        .finally(() => {
          delete pendingDatesRequests.current[requestKey]
        })

      pendingDatesRequests.current[requestKey] = request
      return request
    },
    [datesCacheByLand, setDatesState, setLandDatesCache]
  )

  const getLandImageSnapshot = useCallback(
    (landId, cloudinessFilter = null) => {
      const activeLandImage = getActiveLandImage(landId)
      const selectedDate = activeLandImage.selectedDate || null
      const selectedYieldMapId = activeLandImage.selectedYieldMapId || null
      const imageType = activeLandImage.imageType || null
      const quality = getImageQuality(imageType)
      const datesState = getDatesState(landId, quality)
      const {clearDates, cloudyDatesList} = getLandDatesCollections(landId, cloudinessFilter, quality)
      const yieldMapsState = getLandYieldMapsState(landId)
      const soilMapState = getLandSoilMapState(landId)
      const selectedHdAcquisitionIsEligible = quality !== 'hd' ||
        activeLandImage.selectedAcquisitionStatus === 'ready'

      return {
        activeLandImage,
        selectedDate,
        selectedYieldMapId,
        imageType,
        quality,
        datesState,
        clearDates,
        cloudyDatesList,
        yieldMapsState,
        soilMapState,
        canCreateImageAmbient:
          isImageAmbientSourceType(imageType) &&
          selectedDate != null &&
          selectedHdAcquisitionIsEligible &&
          !loading,
      }
    },
    [
      getActiveLandImage,
      getDatesState,
      getLandDatesCollections,
      getLandSoilMapState,
      getLandYieldMapsState,
      loading,
    ]
  )

  const addOpenedAmbients = useCallback((land_id) => {
    if (!land_id) {
      return
    }

    setOpenedAmbients((prev) => {
      if (prev.includes(land_id)) {
        return prev
      }

      return [...prev, land_id]
    })
  }, [])

  const invalidateLandGeometry = useCallback((nextLand) => {
    const landId = typeof nextLand === 'string' ? nextLand : nextLand?.land_id
    if (!landId) {
      return
    }

    const currentLandImage = activeLands[landId]
    const nextGeometryHash = typeof nextLand === 'object'
      ? nextLand.geometry_hash || nextLand.geometryHash || ''
      : ''
    if (nextGeometryHash) {
      hdGeometryHashByLandRef.current[landId] = nextGeometryHash
    }
    if (currentLandImage?.imageType) {
      mapLayers.land.deleteImage({
        landId,
        imageType: getMapLayerImageType(currentLandImage.imageType),
      })
    }

    mapLayers.land.ambient.remove({landId})
    mapLegendContext.deleteLegend(landId)
    viewImageState.invalidateLandGeometry(landId)
    resetImageLayerLoad(landId)

    delete hdAccessByLandRef.current[landId]
    beginHdAccessRequest(landId)
    setHdAccessByLand((prev) => {
      const next = {...prev}
      delete next[landId]
      return next
    })
    setHdAccessLoadingByLand((prev) => {
      const next = {...prev}
      delete next[landId]
      return next
    })
    setHdAcquisitionsByKey((prev) => omitLandCacheEntries(prev, landId))
    setOpenedAmbients((prev) => prev.filter((openedLandId) => openedLandId !== landId))
    setActiveLands((prev) => {
      const current = prev[landId]
      if (!current) {
        return prev
      }

      return {
        ...prev,
        [landId]: {
          ...current,
          land: typeof nextLand === 'object' ? nextLand : current.land,
          selectedAcquisitionId: null,
          selectedAcquisitionStatus: null,
          selectedAccessId: null,
          selectedCompanyPeriodId: null,
          selectedDate: null,
          selectedItemId: null,
          selectedQualityWarning: null,
          selectedYieldMapId: null,
          remoteSensingSource: null,
        },
      }
    })
    Object.keys(pendingHdAccessRequests.current).forEach((requestKey) => {
      if (requestKey.startsWith(`${landId}:`)) {
        delete pendingHdAccessRequests.current[requestKey]
      }
    })
    setClearMapVersion((prev) => prev + 1)
  }, [activeLands, beginHdAccessRequest, mapLayers.land, mapLegendContext, resetImageLayerLoad])

  const addLand = useCallback(
    (land) => {
      if (!land?.land_id) {
        return
      }

      const existingLandImage = activeLands[land.land_id]
      const existingLand = existingLandImage?.land
      const geometryHash = land.geometry_hash || land.geometryHash || ''
      if (geometryHash) {
        hdGeometryHashByLandRef.current[land.land_id] = geometryHash
      }
      const geometryChanged = hasLandGeometryChanged(existingLand, land)
      if (geometryChanged) {
        invalidateLandGeometry(land)
      }

      setActiveLands((prev) => {
        if (prev[land.land_id] != null) {
          return {
            ...prev,
            [land.land_id]: {
              ...prev[land.land_id],
              land,
            },
          }
        }

        const storedLand = viewImageState.getLand(land.land_id)

        return {
          ...prev,
          [land.land_id]: {
            land,
            imageType:
              storedLand && Object.prototype.hasOwnProperty.call(storedLand, 'imageType')
                ? storedLand.imageType
                : 'rgb',
            selectedDate: storedLand?.selectedDate ?? null,
            selectedYieldMapId: storedLand?.selectedYieldMapId ?? null,
            selectedAcquisitionId: storedLand?.selectedAcquisitionId ?? null,
            selectedAcquisitionStatus: storedLand?.selectedAcquisitionStatus ?? null,
            selectedAccessId: storedLand?.selectedAccessId ?? null,
            selectedCompanyPeriodId: storedLand?.selectedCompanyPeriodId ?? null,
            selectedItemId: storedLand?.selectedItemId ?? null,
            selectedQualityWarning: storedLand?.selectedQualityWarning ?? null,
            remoteSensingSource: storedLand?.remoteSensingSource ?? null,
          },
        }
      })

      ensureLandDates(land.land_id).catch(() => {})
      if (PLANET_HD_V1_ENABLED) {
        ensureLandHdAccess(land.land_id, {
          geometryHash,
          operation: 'view_planet_hd',
        }).catch(() => {})
      }
      ensureLandYieldMaps(land.land_id).catch(() => {})
    },
    [
      activeLands,
      ensureLandDates,
      ensureLandHdAccess,
      ensureLandYieldMaps,
      invalidateLandGeometry,
    ]
  )

  const changeImageType = (land, imageType) => {
    if (loading && imageType != null) {
      return
    }

    const previousImageType = activeLands[land.land_id]?.imageType ?? null
    // An HD index can take several seconds to render. Do not keep the
    // previous index's legend visible while the new layer is loading.
    if (previousImageType !== imageType && isHdImageType(previousImageType)) {
      mapLegendContext.clearBasicLegend(land.land_id)
    }
    const updatedSelection = viewImageState.updateImageType(land.land_id, imageType)
    if (previousImageType !== imageType || !IMAGE_TYPES_WITH_DATE.has(imageType)) {
      resetImageLayerLoad(land.land_id)
    }
    if (imageType === STANDARD_IMAGE_TYPES.YIELD_MAP) {
      ensureLandYieldMaps(land.land_id).catch(() => {})
    }
    if (imageType === STANDARD_IMAGE_TYPES.SOIL_MAP) {
      ensureLandSoilMap(land.land_id).catch(() => {})
    }

    setActiveLands((prev) => {
      const current = prev[land.land_id] || {land, selectedDate: null, selectedYieldMapId: null}
      const selectedYieldMapId =
        imageType === STANDARD_IMAGE_TYPES.YIELD_MAP
          ? current.selectedYieldMapId || yieldMapsByLand[land.land_id]?.[0]?.id || null
          : current.selectedYieldMapId || null

      if (imageType === STANDARD_IMAGE_TYPES.YIELD_MAP) {
        viewImageState.updateYieldMapId(land.land_id, selectedYieldMapId)
      }

      return {
        ...prev,
        [land.land_id]: {
          ...current,
          land,
          imageType,
          selectedDate: updatedSelection?.selectedDate ?? null,
          selectedAcquisitionId: updatedSelection?.selectedAcquisitionId || null,
          selectedAcquisitionStatus: updatedSelection?.selectedAcquisitionStatus || null,
          selectedAccessId: updatedSelection?.selectedAccessId || null,
          selectedCompanyPeriodId: updatedSelection?.selectedCompanyPeriodId || null,
          selectedItemId: updatedSelection?.selectedItemId || null,
          selectedQualityWarning: updatedSelection?.selectedQualityWarning || null,
          remoteSensingSource: updatedSelection?.remoteSensingSource || null,
          selectedYieldMapId,
        },
      }
    })
    return updatedSelection
  }

  const changeYieldMap = (land, yieldMapId) => {
    if (!land?.land_id) {
      return
    }

    viewImageState.updateYieldMapId(land.land_id, yieldMapId)

    setActiveLands((prev) => ({
      ...prev,
      [land.land_id]: {
        ...(prev[land.land_id] || {land, imageType: STANDARD_IMAGE_TYPES.YIELD_MAP, selectedDate: null}),
        land,
        imageType: STANDARD_IMAGE_TYPES.YIELD_MAP,
        selectedYieldMapId: yieldMapId,
      },
    }))
  }

  const changeDate = (land, date) => {
    const selectedDate = viewImageState.updateImageDate(land.land_id, date)

    setActiveLands((prev) => {
      return {
        ...prev,
        [land.land_id]: {
          ...(prev[land.land_id] || {land, imageType: 'rgb'}),
          land,
          selectedDate,
          selectedAcquisitionId: null,
          selectedAcquisitionStatus: null,
          selectedAccessId: null,
          selectedCompanyPeriodId: null,
          selectedItemId: null,
          selectedQualityWarning: null,
        },
      }
    })
  }

  const changeRemoteSensingAcquisition = (land, item, source) => {
    if (!land?.land_id || !item?.date) {
      return
    }

    const normalizedSource = normalizeRemoteSensingSource(source)
    const access = hdAccessByLandRef.current[land.land_id]
    if (PLANET_HD_V1_ENABLED && normalizedSource.product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD && (
      !isPlanetHdDateAllowed(item.date) || !canUsePlanetHd(access) || !doesPlanetHdSelectionMatchAccess(item, access)
    )) return
    const selectedDate = viewImageState.updateRemoteSensingSelection(land.land_id, {
      accessId: item.accessId,
      companyPeriodId: item.companyPeriodId,
      acquisitionId: item.acquisitionId,
      date: item.date,
      itemId: item.itemId,
      qualityWarning: item.qualityWarning || item.reason,
      source: normalizedSource,
      status: item.status,
    })

    setActiveLands((prev) => ({
      ...prev,
      [land.land_id]: {
        ...(prev[land.land_id] || {land}),
        land,
        selectedDate,
        selectedAcquisitionId: item.acquisitionId,
        selectedAcquisitionStatus: item.status,
        selectedAccessId: item.accessId,
        selectedCompanyPeriodId: item.companyPeriodId,
        selectedItemId: item.itemId,
        selectedQualityWarning: item.qualityWarning || item.reason,
        remoteSensingSource: normalizedSource,
      },
    }))
  }

  const getLand = (land_id) => {
    if (activeLands[land_id]) {
      return activeLands[land_id]
    }

    return null
  }

  const hasLandMapOverlay = useCallback(
    (landId) => {
      if (!landId) {
        return false
      }

      const currentLandImage = activeLands[landId]
      const hasSelectedImage = hasRenderableLandLayer(currentLandImage)

      return hasSelectedImage || openedAmbients.includes(landId)
    },
    [activeLands, openedAmbients]
  )

  const hasCurrentLandMapOverlay = useMemo(() => {
    const currentLandId = viewLandContext?.land?.land_id

    if (!currentLandId) {
      return false
    }

    if (viewLandContext?.activeSection === 'images') {
      return hasRenderableLandLayer(activeLands[currentLandId])
    }

    if (viewLandContext?.activeSection === 'ambients') {
      return openedAmbients.includes(currentLandId)
    }

    if (viewLandContext?.activeSection === 'comparison') {
      return hasComparisonMapOverlay
    }

    return false
  }, [
    activeLands,
    hasComparisonMapOverlay,
    openedAmbients,
    viewLandContext?.activeSection,
    viewLandContext?.land?.land_id,
  ])

  const registerComparisonReset = useCallback((handler) => {
    comparisonResetRef.current = handler

    return () => {
      if (comparisonResetRef.current === handler) {
        comparisonResetRef.current = null
      }
    }
  }, [])

  const setComparisonMapOverlayActive = useCallback((active) => {
    setHasComparisonMapOverlay(Boolean(active))
  }, [])

  const closeAllImages = () => {
    Object.keys(activeLands).forEach((key) => {
      changeImageType(activeLands[key].land, null)
    })

    openedAmbients.forEach((land_id) => {
      mapLayers.land.ambient.remove({
        landId: land_id,
      })
    })

    setOpenedAmbients([])
    setActiveAmbientSelection(null)

    if (viewLandContext?.activeSection === 'comparison') {
      comparisonResetRef.current?.()
      setHasComparisonMapOverlay(false)
    }

    setClearMapVersion((prev) => prev + 1)
  }

  useEffect(() => {
    if (viewLandContext?.activeSection === 'ambients') {
      return
    }

    if (openedAmbients.length < 1) {
      return
    }

    openedAmbients.forEach((land_id) => {
      mapLayers.land.ambient.remove({
        landId: land_id,
      })
      mapLegendContext.hideLegend(land_id)
    })

    setOpenedAmbients([])
  }, [
    mapLayers.land.ambient,
    mapLegendContext,
    openedAmbients,
    viewLandContext?.activeSection,
  ])

  useEffect(() => {
    const currentLandId = viewLandContext?.land?.land_id

    setImageActive(Boolean(
      currentLandId &&
      (
        (viewLandContext?.activeSection === 'images' &&
          hasRenderableLandLayer(activeLands[currentLandId])) ||
        (viewLandContext?.activeSection === 'ambients' &&
          openedAmbients.includes(currentLandId))
      )
    ))
  }, [
    activeLands,
    openedAmbients,
    viewLandContext?.activeSection,
    viewLandContext?.land?.land_id,
  ])

  useEffect(() => {
    const previousSection = previousSectionRef.current
    const currentSection = viewLandContext?.activeSection
    const currentLandId = viewLandContext?.land?.land_id

    if (previousSection === 'images' && currentSection === 'layers' && currentLandId) {
      const currentLandImage = activeLands[currentLandId]
      const hasSelectedImage = hasRenderableLandLayer(currentLandImage)

      if (hasSelectedImage) {
        mapLayers.fit(currentLandId)
      }
    }

    previousSectionRef.current = currentSection
  }, [activeLands, mapLayers, viewLandContext?.activeSection, viewLandContext?.land?.land_id])

  useEffect(() => {
    const currentLandId = viewLandContext?.land?.land_id
    if (viewLandContext?.activeSection !== 'images' || !currentLandId) {
      return
    }

    const currentLandImage = activeLands[currentLandId]
    const hasSelectedImage = hasRenderableLandLayer(currentLandImage)

    if (!hasSelectedImage) {
      return
    }

    const restoreImageLegend = () => {
      mapLegendContext.recoverBasicLegend(currentLandId)
      const soilMap = soilMapsByLand[currentLandId]

      if (
        currentLandImage.imageType === STANDARD_IMAGE_TYPES.SOIL_MAP &&
        soilMap?.featureCollection?.type === 'FeatureCollection'
      ) {
        mapLayers.fitToPolygon(soilMap.featureCollection)
        return
      }

      mapLayers.fit(currentLandId)
    }

    window.requestAnimationFrame(restoreImageLegend)
    const timeoutId = window.setTimeout(restoreImageLegend, 180)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    activeLands,
    mapLayers,
    mapLegendContext,
    soilMapsByLand,
    viewLandContext?.activeSection,
    viewLandContext?.land?.land_id,
  ])

  const addLandTiff = (landId, tiffBlob, options = {}) => {
    fromBlob(tiffBlob)
      .then((tiff) => tiff.getImage())
      .then((image) => {
        viewImageState.addLandTiff(landId, image, options)
      })
  }

  const getLandTiff = (landId, options = null) => {
    return viewImageState.getLandTiff(landId, options)
  }

  const value = {
    getImageCloudinessFilter,
    setImageCloudinessFilter,
    dates,
    setDates,
    setDatesState,
    getDatesState,
    getActiveLandImage,
    getRememberedRemoteSensingSelection,
    getLandImageSnapshot,
    getLandDatesCache,
    getLandDatesCollections,
    ensureLandDates,
    ensureLandYieldMaps,
    getLandYieldMapsState,
    ensureLandSoilMap,
    getLandSoilMapState,
    invalidateLandGeometry,
    ensureLandHdAccess,
    getHdAccessStatus,
    requestHdAccess,
    getLandHdAcquisitionsState,
    ensureLandHdAcquisitions,
    prepareLandHdAcquisition,

    cloudyDates,
    setCloudyDates,

    selectedDate,
    changeDate,
    changeRemoteSensingAcquisition,
    changeYieldMap,
    addLandTiff,

    imageType,
    changeImageType,

    activeLands,
    addLand,

    loading,
    setLoading,
    getImageLayerLoadState,
    claimImageLayerAttempt,
    releaseImageLayerAttempt,
    setImageLayerLoading,
    setImageLayerReady,
    setImageLayerError,
    cancelImageLayerLoad,
    retryImageLayer,
    resetImageLayerLoad,

    openPrescription,
    setOpenPrescription: (v) => {
      setOpenPrescription(v)
    },
    openImageAmbient,
    setOpenImageAmbient: (v) => {
      setOpenImageAmbient(v)
    },
    prescriptionAmbient,
    setPrescriptionAmbient,

    getLand,

    closeAllImages,
    clearMapVersion,
    openedAmbients,
    addOpenedAmbients,
    hasImageActive,
    hasLandMapOverlay,
    hasCurrentLandMapOverlay,
    getLandTiff,
    activeAmbientSelection,
    setActiveAmbientSelection,
    registerComparisonReset,
    setComparisonMapOverlayActive,
  }

  return (
    <>
      <ViewImageContext.Provider value={value}>
        {children}
        <PopupEvent />
        <HdAccessConfirmModal
          state={hdConfirmState}
          onCancel={cancelHdAccessConfirmation}
          onConfirm={confirmHdAccess}
        />
        {Object.keys(activeLands).map((landKey) => {
          const {
            imageType,
            selectedAcquisitionId,
            selectedDate,
            selectedItemId,
            selectedYieldMapId,
            remoteSensingSource,
            land,
          } = activeLands[landKey]
          const soilMapData = soilMapsByLand[landKey] || null
          const currentHdAccess = hdAccessByLand[landKey]
          const selectedHdBinding = {
            accessId: activeLands[landKey].selectedAccessId,
            companyPeriodId: activeLands[landKey].selectedCompanyPeriodId,
          }
          const shouldRenderImage =
            viewLandContext?.land?.land_id === land.land_id &&
            viewLandContext?.activeSection === 'images' &&
            (!PLANET_HD_V1_ENABLED || !isHdImageType(imageType) || (
              isPlanetHdDateAllowed(selectedDate) && canUsePlanetHd(currentHdAccess) &&
              doesPlanetHdSelectionMatchAccess(selectedHdBinding, currentHdAccess)
            ))
          const acquisition = selectedAcquisitionId ? {
            acquisitionId: selectedAcquisitionId,
            accessId: selectedHdBinding.accessId || currentHdAccess?.access_id,
            companyPeriodId: selectedHdBinding.companyPeriodId,
            geometryHash: hdAccessByLand[landKey]?.geometry_hash,
            itemId: selectedItemId,
            source: remoteSensingSource,
          } : null
          const key = `${imageType}-${selectedDate}-${selectedAcquisitionId}-${selectedYieldMapId}-${soilMapData?.featureType || soilMapData?.status || 'soil-idle'}-${land.land_id}`
          return (
            <React.Fragment key={`image-provider-parent-${key}`}>
              {shouldRenderImage && imageType === STANDARD_IMAGE_TYPES.EVI && selectedDate != null && (
                <EVI key={key} land={land} selectedDate={selectedDate} />
              )}
              {shouldRenderImage && imageType === HD_IMAGE_TYPES.EVI && selectedDate != null && (
                <EVI key={key} land={land} selectedDate={selectedDate} imageType={imageType} acquisition={acquisition} />
              )}
              {shouldRenderImage && imageType === STANDARD_IMAGE_TYPES.NDVI && selectedDate != null && (
                <NDVI key={key} land={land} selectedDate={selectedDate} />
              )}
              {shouldRenderImage && imageType === HD_IMAGE_TYPES.NDVI && selectedDate != null && (
                <NDVI key={key} land={land} selectedDate={selectedDate} imageType={imageType} acquisition={acquisition} />
              )}
              {shouldRenderImage && imageType === HD_IMAGE_TYPES.NDRE && selectedDate != null && (
                <NDVI key={key} land={land} selectedDate={selectedDate} imageType={imageType} acquisition={acquisition} />
              )}
              {shouldRenderImage && imageType === STANDARD_IMAGE_TYPES.RGB && selectedDate != null && (
                <Visible key={key} land={land} selectedDate={selectedDate} />
              )}
              {shouldRenderImage && imageType === HD_IMAGE_TYPES.RGB && selectedDate != null && (
                <Visible key={key} land={land} selectedDate={selectedDate} imageType={imageType} acquisition={acquisition} />
              )}
              {shouldRenderImage && imageType === 'altimetry' && (
                <Altimetry key={key} land={land} />
              )}
              {shouldRenderImage && imageType === STANDARD_IMAGE_TYPES.SOIL_MAP && soilMapData?.status === 'available' && (
                <SoilMap key={key} land={land} soilMapData={soilMapData} />
              )}
              {shouldRenderImage && imageType === STANDARD_IMAGE_TYPES.YIELD_MAP && selectedYieldMapId != null && (
                <YieldMap key={key} land={land} yieldMapId={selectedYieldMapId} />
              )}
            </React.Fragment>
          )
        })}
      </ViewImageContext.Provider>
    </>
  )
}
